import { useState } from 'react';
import { UserCheck, ArrowRight } from '@/components/icons';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { FormGroup } from '@/components/form/FormGroup';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { convertLead } from '@/api/leads';
import { CUSTOMER_TYPES } from '@/api/price-master';
import type { Lead } from './leadDomain';

/**
 * Converts a lead into a customer in place — the same Party record has its
 * customerType set to the chosen type and its status marked Converted, so it
 * becomes a customer without a duplicate. It then appears in the Enquiry
 * Dashboard's customer picker for future order requests.
 */
export function ConvertLeadModal({
  open, lead, onClose, onConverted,
}: {
  open: boolean;
  lead: Lead | null;
  onClose: () => void;
  onConverted: () => void;
}) {
  const toast = useToast();
  const [customerType, setCustomerType] = useState<string>('');
  const [busy, setBusy] = useState(false);

  if (!lead) return null;
  const effectiveType = customerType || (lead.category || CUSTOMER_TYPES[0]);

  async function handleConvert() {
    if (!lead) return;
    setBusy(true);
    try {
      const updated = await convertLead(Number(lead.id), effectiveType);
      onConverted();
      toast.push(
        `${lead.customer} is now a customer (${updated.convertedCode ?? ''}). Log their next request in the Enquiry Dashboard.`,
        'success',
      );
      onClose();
    } catch (e) {
      toast.push(apiError(e, 'Could not convert lead'), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title={<span className="flex items-center gap-2"><UserCheck size={17} /> Convert Lead to Customer</span>}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="glass" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={handleConvert} disabled={busy} icon={<ArrowRight size={14} />}>
            {busy ? 'Converting…' : 'Convert to Customer'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-[var(--border-soft)] bg-[var(--bg-soft)] p-3 text-sm">
          <div className="font-bold text-[var(--text-primary)]">{lead.customer}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[var(--text-muted)]">
            {lead.category && <Badge tone="accent">{lead.category}</Badge>}
            {lead.contactNumber && <span>{lead.contactNumber}</span>}
            {lead.email && <span>· {lead.email}</span>}
          </div>
        </div>

        <FormGroup label="Customer Type" hint="Carried over from the lead's category — change if needed.">
          <Select value={effectiveType} onChange={(e) => setCustomerType(e.target.value)}>
            {CUSTOMER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
        </FormGroup>

        <p className="text-[11px] text-[var(--text-muted)]">
          This lead record becomes the customer (no duplicate). It will be marked{' '}
          <strong>Converted</strong>; future requests are logged as enquiries in the Enquiry
          Dashboard, not as leads.
        </p>
      </div>
    </Modal>
  );
}
